import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { DogsProvider } from "@/context/DogsContext";
import { Toaster } from "@/components/ui/sonner";

import LandingPage from "@/pages/LandingPage";
import PodcastPage from "@/pages/PodcastPage";
import PodcastEpisodePage from "@/pages/PodcastEpisodePage";
import UnsubscribePage from "@/pages/UnsubscribePage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardLayout from "@/pages/DashboardLayout";
import WelcomePage from "@/pages/WelcomePage";
import OverviewPage from "@/pages/OverviewPage";
import PuppyLogPage from "@/pages/PuppyLogPage";
import VetAndHealthPage from "@/pages/VetAndHealthPage";
import RemindersPage from "@/pages/RemindersPage";
import GetRemindersPage from "@/pages/GetRemindersPage";
import GetStartedPage from "@/pages/GetStartedPage";
import UsingTheCalendarPage from "@/pages/UsingTheCalendarPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import WeeklyPlannerPage from "@/pages/WeeklyPlannerPage";
import ShowPlansPage from "@/pages/ShowPlansPage";
import ShowResultsPage from "@/pages/ShowResultsPage";
import AcademyPage from "@/pages/AcademyPage";
import AssessmentPage from "@/pages/AssessmentPage";
import TrainingPage from "@/pages/TrainingPage";
import GroomingPage from "@/pages/GroomingPage";
import SeniorWellnessPage from "@/pages/SeniorWellnessPage";
import GoalsPage from "@/pages/GoalsPage";
import YearInReviewPage from "@/pages/YearInReviewPage";
import LessonsPage from "@/pages/LessonsPage";
import AdminPage from "@/pages/AdminPage";
import SharingPage from "@/pages/SharingPage";
import SettingsPage from "@/pages/SettingsPage";
import GestationCalculatorPage from "@/pages/breeder/GestationCalculatorPage";
import LittersPage from "@/pages/breeder/LittersPage";
import LitterDetailPage from "@/pages/breeder/LitterDetailPage";
import HeatCyclesPage from "@/pages/breeder/HeatCyclesPage";
import PuppyInvitePage from "@/pages/breeder/PuppyInvitePage";
import InstallHint from "@/components/app/InstallHint";

function ProtectedShell({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  return children;
}

function App() {
  useEffect(() => {
    document.title = "Your Dog Days";
  }, []);

  return (
    <HelmetProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/podcast" element={<PodcastPage />} />
          <Route path="/podcast/:slug" element={<PodcastEpisodePage />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/invite/:token" element={<PuppyInvitePage />} />
          <Route
            element={
              <ProtectedShell>
                <DogsProvider>
                  <DashboardLayout />
                </DogsProvider>
              </ProtectedShell>
            }
          >
            <Route path="/welcome" element={<WelcomePage />} />
            <Route path="/overview" element={<OverviewPage />} />
            <Route path="/puppy-log" element={<PuppyLogPage />} />
            <Route path="/vet-and-health" element={<VetAndHealthPage />} />
            <Route path="/reminders" element={<RemindersPage />} />
            <Route path="/get-reminders" element={<GetRemindersPage />} />
            <Route path="/get-started" element={<GetStartedPage />} />
            <Route path="/using-the-calendar" element={<UsingTheCalendarPage />} />
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/weekly" element={<WeeklyPlannerPage />} />
            <Route path="/lessons" element={<LessonsPage />} />
            <Route path="/show-plans" element={<ShowPlansPage />} />
            <Route path="/show-results" element={<ShowResultsPage />} />
            <Route path="/academy" element={<AcademyPage />} />
            <Route path="/assessment" element={<AssessmentPage />} />
            <Route path="/training" element={<TrainingPage />} />
            <Route path="/grooming" element={<GroomingPage />} />
            <Route path="/senior-wellness" element={<SeniorWellnessPage />} />
            <Route path="/goals" element={<GoalsPage />} />
            <Route path="/sharing" element={<SharingPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/year-in-review" element={<YearInReviewPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/breeder/gestation" element={<GestationCalculatorPage />} />
            <Route path="/breeder/litters" element={<LittersPage />} />
            <Route path="/breeder/litters/:id" element={<LitterDetailPage />} />
            <Route path="/breeder/heat-cycles" element={<HeatCyclesPage />} />
            {/* redirects from old paths */}
            <Route path="/health" element={<Navigate to="/vet-and-health" replace />} />
            <Route path="/vet-records" element={<Navigate to="/vet-and-health" replace />} />
            <Route path="/profile" element={<Navigate to="/overview" replace />} />
            <Route path="/pedigree" element={<Navigate to="/overview" replace />} />
            <Route path="/calendar" element={<Navigate to="/weekly" replace />} />
            <Route path="/journal" element={<Navigate to="/weekly" replace />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" />
        <InstallHint />
      </BrowserRouter>
    </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
